<template>
	<div class="tab-progress-container">
		<!-- Tabs -->
		<div class="tabs d-md-block d-none">
			<div class="tab-container">
				<div
					v-for="(tab, index) in tabs"
					:key="tab.id + index"
					class="tab"
					:class="{ selected: activeTabId === tab.id }"
					@click="handleTabClick(tab.id)"
				>
					{{ tab.title }}
				</div>
			</div>
		</div>

		<div class="tabs mobile d-md-none d-flex">
			<div @click="goBack" class="back-button">
				<InlineSvg src="/images/icons/ic-chevron-left.svg" class="back-icon" />
			</div>
			<div class="tab-container">
				<div
					v-for="(tab, index) in tabs"
					:key="tab.id + index"
					class="tab mobile"
					:class="{ selected: activeTabId === tab.id }"
					@click="handleTabClick(tab.id)"
				>
					{{ tab.title }}
				</div>
			</div>
		</div>

		<!-- Progress Line & Indicators -->
		<div class="progress-line-wrapper d-none d-md-block">
			<div class="progress-line">
				<div class="progress-fill" :style="{ width: fillWidth + '%' }"></div>
			</div>

			<div class="indicators">
				<div v-for="(tab, index) in tabs" :key="'indicator-' + tab.id + index" class="indicator-wrapper">
					<div
						class="indicator"
						:class="{
							complete: touchedTabs.includes(tab.id) && !tab.invalid,
							selected: activeTabId === tab.id,
						}"
					>
						<InlineSvg v-if="touchedTabs.includes(tab.id) && !tab.invalid" src="/images/icons/ic-check.svg" />
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
	import { mapGetters } from 'vuex'

	export default {
		name: 'TabProgress',
		props: {
			tabs: {
				type: Array,
				required: true,
			},
			selectedTabId: {
				type: String,
				default: null,
			},
			isNewPage: {
				type: Boolean,
				default: true,
			},
		},
		data() {
			return {
				localSelectedId: this.tabs[0].id,
				touchedTabs: [this.tabs[0].id],
			}
		},
		watch: {
			selectedTabId(newVal) {
				this.markTabTouched(newVal)
				this.localSelectedId = newVal
			},
		},
		computed: {
			...mapGetters('Auth', ['user']),
			activeTabId() {
				return this.localSelectedId
			},
			fillWidth() {
				let completeTabs = this.tabs.filter(tab => this.touchedTabs.includes(tab.id) && !tab.invalid).length

				const totalParts = this.tabs.length
				if (totalParts === 0 || completeTabs === 0) return 0
				if (completeTabs !== totalParts) {
					completeTabs -= 0.5
				}

				return (completeTabs / totalParts) * 100
			},
		},
		mounted() {
			if (this.selectedTabId) {
				this.markTabTouched(this.selectedTabId)
				this.localSelectedId = this.selectedTabId
			}
			if (!this.isNewPage) {
				this.markAllTabsTouched()
			}
		},
		methods: {
			handleTabClick(id) {
				this.localSelectedId = id
				this.markTabTouched(id)
				const tabs = this.tabs.filter(tab => tab.id === id)
				const tab = tabs.length > 0 ? tabs[0] : undefined
				this.$stanAnalytics('tab-click', {
					meta: { user_id: this.user?.user_id, username: this.user?.username },
					props: { tab_id: id, tab_title: tab?.title },
				})
				this.$emit('tabSelected', id)
			},
			markAllTabsTouched() {
				this.tabs.forEach(tab => {
					this.markTabTouched(tab.id)
				})
			},
			markTabTouched(id) {
				if (!this.touchedTabs.includes(id)) {
					this.touchedTabs.push(id)
				}
			},
			goBack() {
				this.$router.back()
			},
		},
	}
</script>

<style lang="scss" scoped>
	.back-button {
		padding-inline: 8px;
		align-self: center;
		cursor: pointer;
	}
	.tab-progress-container {
		width: 100%;
		font-family: sans-serif;
		background-color: #ffffff;
		position: relative;
		z-index: 10;
	}

	.tabs {
		display: flex;
		gap: 4px;
		justify-content: space-between;
		padding: 12px;
		&.mobile {
			padding-bottom: 1rem;
			padding: 0;
		}
	}

	.tab-container {
		flex: 1;
		display: flex;
		justify-content: space-between;
		align-items: center;
		@media (max-width: 768px) {
			margin-right: 20px;
		}
	}

	.tab {
		text-align: center;
		font-family: Plus Jakarta Sans;
		font-weight: 600;
		font-size: 14px;
		line-height: 14px;
		letter-spacing: 2%;
		padding: 12px;
		border-radius: 8px;
		transition: all 0.1s ease-in;
		cursor: pointer;
		color: var(--stan-gray-dark-color-ada);
		width: 33%;
		max-width: 135px;
		border: 1px solid var(--stan-gray-dark-color);
		&.mobile {
			padding: 16px;
			margin-top: 12px;
			margin-bottom: 12px;
			// border-bottom: 2px solid var(--stan-gray-dark-color);
			max-width: unset;
			width: 48%;
			&.selected {
				font-weight: 700;
				// background: none;
				color: var(--stan-primary-primary-color);
				// border-bottom: 2px solid var(--stan-primary-primary-color);
				border: 1px solid var(--stan-primary-soft-color);
			}
		}
		&.selected {
			background: #d7dbff;
			font-weight: bold;
			border: none;
			color: var(--stan-primary-dark-color);
		}
		&:hover {
			background: #eaebfe;
		}
	}

	.progress-line-wrapper {
		position: relative;
	}

	.progress-line {
		position: absolute;
		top: 8px;
		left: 0;
		right: 0;
		height: 3px;
		background: #dbdbdb;
		z-index: 0;
		border-radius: 4px;
	}

	.progress-fill {
		height: 100%;
		background: var(--stan-primary-primary-color);
		transition: width 0.3s ease;
		border-radius: 4px;
	}

	.indicators {
		display: flex;
		justify-content: space-between;
		position: relative;
		z-index: 1;
	}

	.indicator-wrapper {
		flex: 1;
		display: flex;
		justify-content: center;
		position: relative;
	}

	.indicator::v-deep {
		width: 18px;
		height: 18px;
		background: #fff;
		border: 2px solid #dbdbdb;
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 14px;
		transition: all 0.2s;

		svg path {
			fill: #ffffff;
			stroke: #ffffff;
		}
	}

	.indicator.complete {
		background: var(--stan-primary-primary-color);
		border-color: var(--stan-primary-primary-color);
		color: white;
	}

	.indicator.selected {
		border-color: var(--stan-primary-primary-color);
	}
</style>
